import { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Badge, Button } from '@/components/ui';

interface Property {
  id: string;
  fips: string;
  address: {
    street_number: string;
    street_name: string;
    city: string;
    state: string;
    zip: string;
    full_address: string;
  };
  property_type: string;
  living_area_sqft: number;
  year_built: number;
  bedrooms: number;
  bathrooms: number;
  owner: { name: string; type: string };
  valuation: { market_value: number; tax_amount: number };
  status: string;
}

const US_STATES = [
  { value: '', label: 'All States' },
  { value: 'CA', label: 'California' }, { value: 'TX', label: 'Texas' },
  { value: 'FL', label: 'Florida' }, { value: 'NY', label: 'New York' },
  { value: 'IL', label: 'Illinois' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'OH', label: 'Ohio' }, { value: 'GA', label: 'Georgia' },
  { value: 'NC', label: 'North Carolina' }, { value: 'MI', label: 'Michigan' },
];

export default function SearchPage() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(85000000);
  const [stateFilter, setStateFilter] = useState('CA');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  const searchProperties = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://prisai-api.cyopsys.workers.dev/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          state: stateFilter || undefined,
          limit: 100
        })
      });

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setProperties(data.properties || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [stateFilter]);

  useEffect(() => {
    searchProperties();
  }, [searchProperties]);

  const formatCurrency = (v: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Property Database</h1>
          <p className="text-gray-600">Browse real US property records</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="info" className="text-lg px-4 py-2">
            {total.toLocaleString()} Total
          </Badge>
          <div className="flex gap-2">
            <button onClick={() => setViewMode('cards')} className={`px-4 py-2 rounded-lg ${viewMode === 'cards' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>Cards</button>
            <button onClick={() => setViewMode('table')} className={`px-4 py-2 rounded-lg ${viewMode === 'table' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>Table</button>
          </div>
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              >
                {US_STATES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <Button onClick={searchProperties} disabled={loading}>
              {loading ? 'Loading...' : 'Search'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error: {error}
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading properties...</p>
        </div>
      )}

      {!loading && properties.length > 0 && viewMode === 'cards' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {properties.map((property) => (
            <Card key={property.id}>
              <CardBody>
                <div className="flex justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{property.address.full_address}</h3>
                    <Badge variant="success">{property.status}</Badge>
                    <div className="mt-2 text-sm text-gray-600 grid grid-cols-2 gap-2">
                      <div>Type: {property.property_type}</div>
                      <div>Beds: {property.bedrooms}</div>
                      <div>Baths: {property.bathrooms}</div>
                      <div>Sqft: {property.living_area_sqft?.toLocaleString()}</div>
                      <div>Owner: {property.owner?.name}</div>
                      <div>ZIP: {property.address.zip}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-green-600">{formatCurrency(property.valuation?.market_value)}</div>
                    <div className="text-xs text-gray-500">Tax: {formatCurrency(property.valuation?.tax_amount)}</div>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {!loading && properties.length > 0 && viewMode === 'table' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Beds</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Baths</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {properties.map((property) => (
                  <tr key={property.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">{property.address.street_number} {property.address.street_name}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{property.address.city}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{property.address.state}</td>
                    <td className="px-4 py-2 text-sm text-right font-medium text-green-600">{formatCurrency(property.valuation?.market_value)}</td>
                    <td className="px-4 py-2 text-sm text-center">{property.bedrooms}</td>
                    <td className="px-4 py-2 text-sm text-center">{property.bathrooms}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{property.owner?.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && properties.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Select a state and click Search</p>
        </div>
      )}
    </div>
  );
}
